import axios from 'axios';
import { parseDmsSyncStatus } from '@/general/converters/dmsConverters';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import { parseJobs } from '@/jobs/converters/jobConverters';
import Job from '@/jobs/models/Job';
import { formatMoment } from '@/shared/converters/dateConverters';
import { parseTags } from '@/shared/converters/tagConverters';
import AssignmentData from '@/shared/models/AssignmentData';
import DmsSyncStatus from '@/shared/models/DmsSyncStatus';
import { OutletId } from '@/shared/models/Outlet';
import Tag from '@/shared/models/Tag';
import { Url } from '@/shared/models/common';
import { isPresent, present } from '@/shared/utils/typescript';
import VehicleStatus from '../constants/VehicleStatus';
import WorkStatus from '../constants/WorkStatus';
import { formatCheckInData, formatCheckInData2, formatDossierOverviewRequest, formatDossierTags, formatPickedUpData, formatReadyForPickupData, parseDossier } from '../converters/dossierConverters';
import { parseDossierEvents } from '../converters/dossierEventConverters';
import { CleaningData, DossierId, DossierReference } from '../models/AbstractDossier';
import { CheckInData } from '../models/CheckInData';
import Dossier, { AppointmentData } from '../models/Dossier';
import DossierEvent from '../models/DossierEvent';
import DossierOverviewRequest from '../models/DossierOverviewRequest';
import { PickedUpData } from '../models/PickedUpData';
import { ReadyForPickupClientData } from '../models/ReadyForPickupData';
import CheckInDetails from '@/dossier/models/CheckInDetails';
import { parseCheckInDetails } from '@/dossier/converters/checkInConverters';
import { CheckInFormData } from '@/dossier/models/CheckInContext';

export interface WorkStatusData {
  tag?: Tag;
  isReadyForPickup?: boolean;
  jobs?: Array<Job>;
}

enum StatusType {
  WORK = 'WORK',
  VEHICLE = 'OBJECT',
}

export interface ActiveDossierResult {
  dossierId: DossierId;
  externalReference: DossierReference;
  outletId: OutletId;
}

export default class DossierClient {
  static async findById(id: DossierId): Promise<Dossier> {
    const response = await axios.get(`/api/mobopro/v3/dossiers/${id}`);
    return present(parseDossier(response.data));
  }

  static async checkOtherActive(id: DossierId): Promise<ActiveDossierResult | null> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${id}/active`);
    if (!isPresent(response.data.dossierId)) return null;
    return {
      dossierId: response.data.dossierId,
      externalReference: response.data.externalReference ?? null,
      outletId: response.data.outletId,
    };
  }

  static async getCheckInDetails(id: DossierId): Promise<CheckInDetails> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${id}/checkindetails`);
    return present(parseCheckInDetails(response.data));
  }

  static async getDossierEvents(dossierId: DossierId): Promise<Array<DossierEvent>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/events`);
    return parseDossierEvents(response.data);
  }

  static async getTooltipJobs(dossierId: DossierId): Promise<Array<Job>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobstooltip`);
    return parseJobs(response.data);
  }

  static async updateTags(id: DossierId, tags: Array<Tag>): Promise<Array<Tag>> {
    const response = await axios.put(`/api/mobopro/v1/dossiers/${id}/tags`, formatDossierTags(tags));
    return present(parseTags(response.data.tags));
  }

  static async updateLease(id: DossierId, enabled: boolean): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${id}`, {
      isLeasing: enabled,
    });
  }

  static async markFluidLevelsChecked(id: DossierId, checked: boolean): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${id}`, {
      isFluidsLevelChecked: checked,
    });
  }

  static async updateVisibility(id: DossierId, isVisible: boolean): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${id}`, {
      isSignable: isVisible,
    });
  }

  static async updateAppointment(dossierId: DossierId, data: AppointmentData): Promise<void> {
    await DossierClient.doUpdateAppointment(dossierId, data);
    if (data.transportIn.byCustomer && data.transportOut.byCustomer) {
      await DossierClient.deletePickupDelivery(dossierId);
    } else {
      await DossierClient.updatePickUpDelivery(dossierId, data);
    }
  }

  private static async doUpdateAppointment(dossierId: DossierId, data: AppointmentData): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}`, {
      waitsOnPremise: data.waitsOnPremise,
      preferredPickUp: data.transportOut.byCustomer ? formatMoment(data.transportOut.time) : null,
      mobilitySolutionId: data.mobilitySolution?.id || null,
    });
  }

  static async updateNote(dossierId: DossierId, note: string | null): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}`, { note });
  }

  private static async updatePickUpDelivery(dossierId: DossierId, data: AppointmentData): Promise<void> {
    const pickup = data.transportIn.byCustomer ? null : data.transportIn;
    const delivery = data.transportOut.byCustomer ? null : data.transportOut;
    await axios.put(`/api/mobopro/v1/dossiers/${dossierId}/pickupdelivery`, {
      pickUpAddress: pickup?.address ?? null,
      pickUpPlanning: formatMoment(pickup?.time),
      pickUpOutletUserId: pickup?.driver?.id ?? null,
      pickUpAdditionalInfo: pickup?.note || null,
      hasNoPickUp: !pickup,
      deliveryAddress: delivery?.address ?? null,
      deliveryPlanning: formatMoment(delivery?.time),
      deliveryOutletUserId: delivery?.driver?.id ?? null,
      deliveryAdditionalInfo: delivery?.note || null,
      hasNoDelivery: !delivery,
    });
  }

  private static async deletePickupDelivery(dossierId: DossierId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/pickupdelivery`);
  }

  static async updateCleaning(id: DossierId, data: CleaningData): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${id}`, {
      cleaningStatus: data.exteriorStatus,
      ...(data.exteriorMethod && { cleaningMethod: data.exteriorMethod }),
      ...(data.interiorStatus && { interiorCleaningStatus: data.interiorStatus }),
    });
  }

  static async updateWorkStatus(dossierId: DossierId, status: WorkStatus, data?: WorkStatusData): Promise<DmsSyncStatus | null> {
    const extraData: any = {};
    if (status === WorkStatus.BEING_HANDLED) extraData.workStarted = { jobsStarted: data?.jobs?.map(j => j.id) ?? [] };
    if (status === WorkStatus.ON_HOLD && data) {
      extraData.workOnHoldInfo = {
        tagId: data.tag?.id,
        isReadyForPickup: data.isReadyForPickup ?? undefined,
      };
    }
    return parseDmsSyncStatus(await DossierClient.doUpdateStatus(dossierId, StatusType.WORK, status, extraData));
  }

  static async updateVehicleStatus(dossierId: DossierId, status: VehicleStatus): Promise<void> {
    await DossierClient.doUpdateStatus(dossierId, StatusType.VEHICLE, status);
  }

  static async markVehicleCheckedIn(data: CheckInData, dossier: Dossier): Promise<void> {
    await DossierClient.doUpdateStatus(dossier.id, StatusType.VEHICLE, VehicleStatus.CHECKED_IN, formatCheckInData(data, dossier));
  }

  static async markVehicleCheckedIn2(data: CheckInFormData, dossier: Dossier): Promise<void> {
    await DossierClient.doUpdateStatus(dossier.id, StatusType.VEHICLE, VehicleStatus.CHECKED_IN, formatCheckInData2(data, dossier));
  }

  static async markVehicleReadyForPickup(dossierId: DossierId, data: ReadyForPickupClientData): Promise<void> {
    await DossierClient.doUpdateStatus(dossierId, StatusType.VEHICLE, VehicleStatus.READY_FOR_PICKUP, formatReadyForPickupData(data));
  }

  static async markVehiclePickedUp(data: PickedUpData, dossierId: DossierId): Promise<void> {
    await DossierClient.doUpdateStatus(dossierId, StatusType.VEHICLE, VehicleStatus.PICKED_UP, formatPickedUpData(data));
  }

  private static async doUpdateStatus(dossierId: DossierId, statusType: StatusType, statusValue: number, extraData?: object): Promise<void> {
    await axios.put(`/api/mobopro/v2/dossiers/${dossierId}/status`, {
      statusType,
      statusValue,
      ...extraData,
    });
  }

  static async updateAssignment(dossierId: DossierId, assignment: AssignmentData): Promise<Dossier> {
    const data: any = {};
    if (assignment) {
      if ('userId' in assignment) data.outletUserId = assignment.userId;
      if ('groupId' in assignment) data.assignToGroup = assignment.groupId;
    }
    const response = await axios.put(`/api/mobopro/v1/dossiers/${dossierId}/assignment`, data);
    return present(parseDossier(response.data));
  }

  static async updateWorkshopTeam(dossierId: DossierId, workshopPlanningTeamId: WorkshopTeamId | null): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}`, { workshopPlanningTeamId });
  }

  static async createOverview(data: DossierOverviewRequest, dossierId: DossierId): Promise<Url> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/overviewpages`, formatDossierOverviewRequest(data));
    return response.data.pageURL;
  }

  static async updateInvoicedStatus(dossierId: DossierId, isInvoiced: boolean): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}`, {
      isInvoiced,
    });
  }

  static async updatePreCheckin(dossierId: DossierId, value: boolean): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}`, {
      isPreCheckinExcluded: !value,
    });
  }

  static async remove(dossierId: DossierId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}`);
  }
}